<template>
  <transition name="fade">
    <AlertConfirmation
      v-if="alert_confirmation"
      @confirmNo="handle_clicked_no"
      @confirmYes="handle_clicked_yes"
      :animation_class="animation_alert_confirmation_class"
      text="Are you sure you want to delete this Product?"
      text_class="text-xl font-medium"
      title="Delete Product"
    />
  </transition>
  <section class="grid grid-cols-2 lg:grid-cols-4 gap-4 pb-96 h-fit">
    <PageHeader
      class="col-span-2 sm:col-span-1 lg:col-span-2"
      title="Product"
      subtitle="All Product"
    />
    <div class="col-span-2 sm:col-span-1 lg:col-span-2 flex justify-end items-end">
      <ButtonRouter id="new-product" button_text="&#43; New Product" path="/product/new"/>
    </div>
    <div class="content col-span-2 lg:col-span-4">
      <div class="row-start-2 col-span-2 lg:col-span-4">
        <div class="flex flex-col gap-2">
          <Select
            select_class="w-56 mb-4"
            option_placeholder="All Category"
            :option_placeholder_selected="false"
            :option_placeholder_disabled="false"
            valueKey="id"
            labelKey="name"
            :options="allCategories"
            v-model="filter.category_id"
            @update:modelValue="filterByCategory"
          />
        </div>
      </div>
      <AlertSuccess v-if="alert_success" :text="alert_text" />
      <Card>
        <DinamicTable 
          :header_columns="table_header" 
          :sortable_columns="sortable_columns"
          v-model="sortConfig"
          th_class="text-left"
          @update:modelValue="handleSort"
        >
          <TableRow v-if="allProducts.length == 0">
            <td colspan="7" class="p-3 text-center italic text-white tracking-widest"> ~ Belum ada data ~ </td>
          </TableRow>
          <TableRow v-for="(product, index) in sortedProducts" :key="index">
            <TableCell>{{ index + 1 + (pagination.page - 1) * pagination.perPage }}</TableCell>
            <TableCell>{{ product?.products_name }}</TableCell>
            <TableCell>
              <div class="flex gap-2 flex-wrap items-center">
                <template v-for="(category, index) in product.categories" :key="index">
                  <span v-if="categoryName(category?.id) != ''" class="bg-white/30 px-2 py-1 rounded-md">
                    {{ categoryName(category?.id) }}
                  </span>
                </template>
              </div>
            </TableCell>
            <TableCell>{{ formatRupiah(product?.price) }}</TableCell>
            <td class="flex gap-2 py-1">
              <ButtonIcon id="btnEditProduct" :path="`/product/edit/${product?.id}`">
                <PencilIcon class="h-5 w-5 text-black/50" />
              </ButtonIcon>
              <ButtonIcon @click="deleteProduct(product?.id)" id="btnDeleteProduct" path="#">
                <TrashIcon class="h-5 w-5 text-black/50"/>
              </ButtonIcon>
            </td>
          </TableRow>
        </DinamicTable>
      </Card>
    </div>
  </section>
</template>

<script>
import ButtonRouter from '@/components/buttons/ButtonRouter.vue';
import DinamicTable from '@/components/organisms/DinamicTable.vue';
import TableRow from "@/components/atoms/table/TableRow.vue";
import TableCell from "@/components/atoms/table/TableCell.vue";
import Card from '@/components/organisms/Card.vue';
import PageHeader from "@/components/molecules/PageHeader.vue";
import ButtonIcon from '@/components/buttons/ButtonIcon.vue';
import AlertConfirmation from "@/components/alerts/AlertConfirmation.vue";
import AlertSuccess from '@/components/alerts/AlertSuccess.vue';
import Pagination from "@/components/organisms/Pagination.vue";
import { formatRupiah, excerpt } from '@/helpers/formating';
import { TrashIcon, PencilIcon } from '@heroicons/vue/24/solid';
import { mapGetters, mapActions } from 'vuex';
import Select from '@/components/form/Select.vue';

export default {
  name: 'Product',
  components: {
    ButtonRouter, Select, DinamicTable, TableRow, TableCell, Pagination, AlertSuccess, Card, ButtonIcon, AlertConfirmation, PageHeader,
    TrashIcon, PencilIcon
  },
  data() {
    return {
      table_header: [
        'No',
        'Product Name',
        'Category',
        'Price',
        'Action'
      ],

      sortable_columns: ['Product Name', 'Price'], // Columns that can be sorted
      sortConfig: {
        column: '',
        direction: '',
      },

      filter: {
        category_id: '',
      },
      isFiltering: false,

      alert_success: false,
      alert_text: '',
      alert_confirmation: false,
      animation_alert_confirmation_class: "",
      product_id_to_delete: null
    }
  },
  async created() {
    await this.$store.dispatch('products/fetchProducts');
    await this.$store.dispatch('categories/fetchCategories');
  },
  computed: {
    ...mapGetters('products', ['allProducts', 'pagination']),
    ...mapGetters('categories', ['allCategories']),

    filteredProduct() {
      let products = this.allProducts;

      if (this.filter.category_id) {
        products = products.filter(product =>
          product.categories ? product.categories.some(category => category.id == this.filter.category_id) : false
        );
      }

      return products;
    },

    sortedProducts() {
      if (!this.sortConfig.column || !this.sortConfig.direction) {
        return this.filteredProduct;
      }

      const sortedData = [...this.filteredProduct];
      
      // Mapping table headers to actual data fields
      const columnMap = {
        'Product Name': 'products_name',
        'Category': 'Category.name',
        'Price': 'price'
      };

      const field = columnMap[this.sortConfig.column];
      
      return sortedData.sort((a, b) => {
        let valueA = field.includes('.') ? 
          field.split('.').reduce((obj, key) => obj?.[key], a) : 
          a[field];
        let valueB = field.includes('.') ? 
          field.split('.').reduce((obj, key) => obj?.[key], b) : 
          b[field];

        // Handle null/undefined values
        valueA = valueA ?? '';
        valueB = valueB ?? '';

        // Special handling for different data types
        if (field === 'price') {
          // Convert to numbers for price comparison
          valueA = parseFloat(valueA) || 0;
          valueB = parseFloat(valueB) || 0;
        } else {
          // Convert to lowercase strings for text comparison
          valueA = String(valueA).toLowerCase();
          valueB = String(valueB).toLowerCase();
        }

        // Natural sorting logic
        if (this.sortConfig.direction === 'asc') {
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        } else {
          return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
        }
      });
    }
  },
  methods: {
    formatRupiah, excerpt,
    ...mapActions('products', ['fetchProducts', 'deleteProductById']),

    filterByCategory() {
      this.isFiltering = this.filter.category_id != '';
    },

    handleSort(sortConfig) {
      this.sortConfig = sortConfig;
    },
    categoryName(id) {
      return this.allCategories.find(category => category.id == id)?.name ?? '';
    },
    deleteProduct(id) {
      this.product_id_to_delete = id;
      this.alert_confirmation = true;
    },
    handle_clicked_no() {
      this.alert_confirmation = false;
      this.animation_alert_confirmation_class = "animate__backOutUp";
      this.product_id_to_delete = null;
    },
    handle_clicked_yes() {
      this.alert_confirmation = false;
      this.animation_alert_confirmation_class = "animate__backOutUp";
      this.deleteProductById(this.product_id_to_delete);
      this.fetchProducts(1);
      this.product_id_to_delete = null;
      this.alert_success = true;
      this.alert_text = "Product deleted successfully!";
      setTimeout(() => {
        this.alert_success = false;
        this.alert_text = "";
      }, 1000);
    },
  }
}
</script>